"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function (o, m, k, k2) {
    if (k2 === undefined)
        k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
        desc = { enumerable: true, get: function () { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function (o, m, k, k2) {
    if (k2 === undefined)
        k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function (o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function (o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function (o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o)
                if (Object.prototype.hasOwnProperty.call(o, k))
                    ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule)
            return mod;
        var result = {};
        if (mod != null)
            for (var k = ownKeys(mod), i = 0; i < k.length; i++)
                if (k[i] !== "default")
                    __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ClassificationProductTypeEditCell = void 0;
const react_1 = __importStar(require("react"));
const ui_components_1 = require("@groupby/ui-components");
const classnames_1 = __importDefault(require("classnames"));
const classification_product_type_edit_cell_styles_1 = require("./classification-product-type-edit-cell.styles");
const attributes_1 = require("../../../../../../client/attributes");
const product_tree_nodes_1 = require("../../../../../../client/product-tree-nodes");
const generic_1 = require("../../../../../../generic");
const hooks_1 = require("../../../../../../hooks");
const localization_1 = require("../../../../../../../localization");
const store_1 = require("../../../../../../../store");
const CATEGORY_SELECT_DROPDOWN_OFFSET = 115; // Manually selected value for optimal dropdown positioning
/**
 * Edit cell component for product classification in new MUI DataGrid.
 *
 * This component provides:
 * - Inline editing interface for product classification categories
 * - Tree node selection with search and load-more capabilities
 * - Integration with MUI DataGrid edit mode APIs
 * - Commit changes via DataGrid's setEditCellValue, routed to processRowUpdate
 * - Respects per-cell loading from table config to disable controls and show a loading label
 *
 * **Usage:** Used in new table's `renderEditCell` for classification product type columns.
 * Automatically integrated via `parseColumn.tsx` configuration for classification columns.
 *
 * **Features:**
 * - Searchable dropdown with product tree nodes
 * - Keyboard navigation (Enter to commit, Escape to cancel)
 * - Proper positioning within DataGrid cell constraints
 * - Changes routed through processRowUpdate for unified persistence
 *
 * @example
 * ```tsx
 * // Automatically used via parseColumn.tsx
 * // When column.path === CustomProductAttributes.ClassificationProductType
 * {
 *   renderEditCell: (params) => (
 *     <ClassificationProductTypeEditCell
 *       value={params.value}
 *       productId={params.row.id}
 *       field={params.field}
 *       isLoading={isCellLoading({ id: params.id, field: params.field })}
 *     />
 *   )
 * }
 * ```
 */
const ClassificationProductTypeEditCell = ({ value, productId, field, isLoading = false, }) => {
    const [selectedNodeValue, setSelectedNodeValue] = (0, react_1.useState)(value);
    const { formatMessage: t } = (0, localization_1.useLocalization)();
    const wrapperRef = react_1.default.useRef(null);
    const filterInputRef = react_1.default.useRef(null);
    const apiRef = (0, ui_components_1.useAdvancedGridApiContext)();
    const productTreeId = (0, store_1.useProductTreeId)();
    const { data: terminalNodes, isLoading: productTreeNodesLoading, search: { onSearch, }, pagination: { hasNextPage, isLoadingMore, onNextPage, }, } = (0, product_tree_nodes_1.useProductTreeNodes)({
        productTreeId,
        filter: {
            terminalNodesOnly: true,
        },
        pageSize: 10,
    });
    const { searchTerm, onSearchChange, } = (0, hooks_1.useSearch)({ onSearch });
    const lastElementRef = (0, hooks_1.useLoadMore)({
        moreItemsLoading: isLoadingMore,
        hasNextPage,
        onLoadMore: onNextPage,
    });
    const terminalNodeOptions = (0, react_1.useMemo)(() => {
        const terminalNodeOptions = terminalNodes.map((item) => (item.title === attributes_1.UN_CATEGORIZED_NODE_TITLE
            ? { label: t({ key: 'PRODUCT_RECLASSIFY_UNCATEGORIZED_LABEL' }), value: item.title }
            : { label: item.title, value: item.title }))
            .sort((a, b) => {
            if (a.value === attributes_1.UN_CATEGORIZED_NODE_TITLE)
                return -1;
            if (b.value === attributes_1.UN_CATEGORIZED_NODE_TITLE)
                return 1;
            return 0;
        });
        if (!terminalNodeOptions.map((n) => n.label).includes(selectedNodeValue)) {
            terminalNodeOptions.unshift({ label: selectedNodeValue, value: selectedNodeValue });
        }
        return terminalNodeOptions;
    }, [terminalNodes, selectedNodeValue, t]);
    const selectedNode = (0, react_1.useMemo)(() => terminalNodes.find((terminalNode) => terminalNode.title === selectedNodeValue), [terminalNodes, selectedNodeValue]);
    (0, react_1.useEffect)(() => {
        if (!selectedNodeValue && selectedNode) {
            setSelectedNodeValue(selectedNode.title);
        }
    }, [selectedNode, selectedNodeValue]);
    const handleCancel = (0, react_1.useCallback)(() => {
        setSelectedNodeValue(value); // Reset to original value
        apiRef.current.stopCellEditMode({
            id: productId,
            field,
        });
    }, [apiRef, field, productId, value]);
    const handleSave = (0, react_1.useCallback)(() => __awaiter(void 0, void 0, void 0, function* () {
        if (selectedNodeValue === value) {
            handleCancel(); // No changes, just cancel
            return;
        }
        const targetCategory = (selectedNode === null || selectedNode === void 0 ? void 0 : selectedNode.title) || selectedNodeValue;
        // Commit the new value via DataGrid's setEditCellValue
        // This triggers processRowUpdate on commit
        yield apiRef.current.setEditCellValue({ id: productId, field, value: targetCategory }, { debounceMs: 0 });
        apiRef.current.stopCellEditMode({
            id: productId,
            field,
        });
    }), [selectedNodeValue, value, selectedNode, apiRef, productId, field, handleCancel]);
    const defaultOptions = [{
            label: t({ key: 'LOADING' }),
            value: null,
            disabled: true,
        }];
    const handleSelectChange = (0, react_1.useCallback)((event) => {
        if (event.target.value) {
            setSelectedNodeValue(event.target.value);
        }
    }, []);
    // Extract keyboard logic for better readability and maintainability
    const handleMenuKeyDown = (0, react_1.useCallback)((e) => {
        var _a;
        // Prevent infinite loop by checking if event came from our input
        if (e.target === filterInputRef.current) {
            return; // Don't re-dispatch events that came from our input
        }
        e.stopPropagation();
        // Handle Escape key to cancel editing
        if (e.key === 'Escape') {
            handleCancel();
            return;
        }
        // Forward typing keys (printable characters, backspace, delete) to search input
        if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete') {
            (_a = filterInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            // Simulate typing in the input instead of dispatching the original event
            if (e.key.length === 1) {
                // For printable characters, update the input value directly
                const input = filterInputRef.current;
                if (input) {
                    const currentValue = input.value;
                    const newValue = currentValue + e.key;
                    input.value = newValue;
                    // Trigger onChange event
                    const changeEvent = new Event('input', { bubbles: true });
                    input.dispatchEvent(changeEvent);
                }
            }
        }
    }, [handleCancel]);
    return (react_1.default.createElement(classification_product_type_edit_cell_styles_1.StyledWrapper, { ref: wrapperRef }, react_1.default.createElement(classification_product_type_edit_cell_styles_1.CategorySelectMenuGlobalStyles, null), react_1.default.createElement(generic_1.Select, { options: productTreeNodesLoading ? defaultOptions : terminalNodeOptions, onChange: handleSelectChange, value: selectedNodeValue, renderValue: (value) => (productTreeNodesLoading ? selectedNodeValue : value), className: "category-select", fullWidth: true, disabled: isLoading,
        // Auto-focus search input when dropdown opens for better UX
        onOpen: () => {
            // Use setTimeout to ensure the search input is rendered before focusing
            setTimeout(() => {
                var _a;
                (_a = filterInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 100); // Slightly longer delay for MUI v5
        }, MenuProps: {
            className: (0, classnames_1.default)('category-select-menu'),
            anchorOrigin: {
                vertical: 'bottom',
                horizontal: CATEGORY_SELECT_DROPDOWN_OFFSET, // Manually selected value for optimal dropdown positioning
            },
            autoFocus: false,
            disableEnforceFocus: true,
            disableAutoFocus: true,
            disableAutoFocusItem: true,
            // Prevent menu list from intercepting keypress events in our custom search input
            // Components there are Menu -> MenuList -> MenuItem's
            // MenuList intercepts keypresses for its own navigation, but we want them
            // to go to our search input instead. Extract logic to separate function.
            onKeyDownCapture: handleMenuKeyDown,
        }, lastOptionRef: lastElementRef }, react_1.default.createElement("div", { className: "search-input-wrapper" }, react_1.default.createElement(classification_product_type_edit_cell_styles_1.StyledSearchInput, { inputRef: filterInputRef, placeholder: t({ key: 'SEARCH' }), variant: "outlined", value: searchTerm, onChange: onSearchChange, size: "small", onKeyDown: (e) => {
            e.stopPropagation();
        } }))), react_1.default.createElement(classification_product_type_edit_cell_styles_1.StyledSaveButton, { variant: "contained", onClick: handleSave, disabled: isLoading }, isLoading ? t({ key: 'SAVING' }) : t({ key: 'SAVE' }))));
};
exports.ClassificationProductTypeEditCell = ClassificationProductTypeEditCell;
